// Subscription Validation Utility
// Validation functions for subscription system data integrity

/**
 * Validate subscription data structure
 * @param {Object} subscription - Subscription data to validate
 * @returns {Object} - Validation result
 */
export const validateSubscriptionStructure = (subscription) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!subscription) {
      errors.push('Subscription data is required');
      return { isValid: false, errors, warnings };
    }
    
    // Validate basePlan
    if (!subscription.basePlan) {
      errors.push('Base plan is required');
    } else {
      const basePlanErrors = validateBasePlan(subscription.basePlan);
      errors.push(...basePlanErrors);
    }
    
    // Validate topups array
    if (!Array.isArray(subscription.topups)) {
      errors.push('Topups must be an array');
    } else {
      subscription.topups.forEach((topup, index) => {
        const topupErrors = validateTopup(topup);
        if (topupErrors.length > 0) {
          errors.push(`Topup ${index}: ${topupErrors.join(', ')}`);
        }
      });
    }
    
    // Validate totalEntitlements
    if (!subscription.totalEntitlements) {
      warnings.push('Total entitlements not calculated');
    } else {
      const entitlementsErrors = validateEntitlements(subscription.totalEntitlements);
      errors.push(...entitlementsErrors);
    }
    
    // Validate currentMonthUsage
    if (!subscription.currentMonthUsage) {
      warnings.push('Current month usage not initialized');
    } else {
      const usageErrors = validateUsage(subscription.currentMonthUsage);
      errors.push(...usageErrors);
    }
    
    // Validate dates
    if (subscription.basePlan) {
      const dateErrors = validateDates(subscription.basePlan);
      errors.push(...dateErrors);
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating subscription structure:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};

/**
 * Validate base plan data
 * @param {Object} basePlan - Base plan data
 * @returns {Array} - Array of error messages
 */
export const validateBasePlan = (basePlan) => {
  const errors = [];
  
  if (!basePlan.planId) {
    errors.push('Plan ID is required');
  }
  
  if (!basePlan.planName) {
    errors.push('Plan name is required');
  }
  
  if (typeof basePlan.price !== 'number' || basePlan.price < 0) {
    errors.push('Price must be a non-negative number');
  }
  
  if (!basePlan.limits) {
    errors.push('Plan limits are required');
  } else {
    const limitsErrors = validateLimits(basePlan.limits);
    errors.push(...limitsErrors);
  }
  
  if (!basePlan.startDate) {
    errors.push('Start date is required');
  }
  
  if (!basePlan.nextResetDate) {
    errors.push('Next reset date is required');
  }
  
  if (typeof basePlan.validityDays !== 'number' || basePlan.validityDays <= 0) {
    errors.push('Validity days must be a positive number');
  }
  
  return errors;
};

/**
 * Validate limits object
 * @param {Object} limits - Limits data
 * @returns {Array} - Array of error messages
 */
export const validateLimits = (limits) => {
  const errors = [];
  const requiredFields = ['firebase_operations', 'storage_mb', 'sales_documents', 'parties'];
  
  requiredFields.forEach(field => {
    if (typeof limits[field] !== 'number' || limits[field] < 0) {
      errors.push(`${field} must be a non-negative number`);
    }
  });
  
  return errors;
};

/**
 * Validate topup data
 * @param {Object} topup - Topup data
 * @returns {Array} - Array of error messages
 */
export const validateTopup = (topup) => {
  const errors = [];
  
  if (!topup.id && !topup.topupId) {
    errors.push('Topup ID is required');
  }
  
  if (!topup.name) {
    errors.push('Topup name is required');
  }
  
  if (typeof topup.price !== 'number' || topup.price < 0) {
    errors.push('Topup price must be a non-negative number');
  }
  
  if (!topup.limits) {
    errors.push('Topup limits are required');
  } else {
    const limitsErrors = validateLimits(topup.limits);
    errors.push(...limitsErrors);
  }
  
  if (!topup.status || !['active', 'exhausted', 'expired'].includes(topup.status)) {
    errors.push('Topup status must be active, exhausted, or expired');
  }
  
  if (!topup.purchaseDate) {
    errors.push('Purchase date is required');
  }
  
  if (topup.remaining && typeof topup.remaining !== 'object') {
    errors.push('Remaining must be an object');
  }
  
  return errors;
};

/**
 * Validate entitlements data
 * @param {Object} entitlements - Entitlements data
 * @returns {Array} - Array of error messages
 */
export const validateEntitlements = (entitlements) => {
  const errors = [];
  
  if (typeof entitlements !== 'object') {
    errors.push('Entitlements must be an object');
    return errors;
  }
  
  const requiredFields = ['firebase_operations', 'storage_mb', 'sales_documents', 'parties'];
  
  requiredFields.forEach(field => {
    if (typeof entitlements[field] !== 'number' || entitlements[field] < 0) {
      errors.push(`Entitlements ${field} must be a non-negative number`);
    }
  });
  
  return errors;
};

/**
 * Validate usage data
 * @param {Object} usage - Usage data
 * @returns {Array} - Array of error messages
 */
export const validateUsage = (usage) => {
  const errors = [];
  
  if (typeof usage !== 'object') {
    errors.push('Usage must be an object');
    return errors;
  }
  
  const requiredFields = ['operations', 'storage', 'documents', 'users'];
  
  requiredFields.forEach(field => {
    if (typeof usage[field] !== 'number' || usage[field] < 0) {
      errors.push(`Usage ${field} must be a non-negative number`);
    }
  });
  
  return errors;
};

/**
 * Validate dates in base plan
 * @param {Object} basePlan - Base plan data
 * @returns {Array} - Array of error messages
 */
export const validateDates = (basePlan) => {
  const errors = [];
  
  try {
    const startDate = new Date(basePlan.startDate);
    const nextResetDate = new Date(basePlan.nextResetDate);
    
    if (isNaN(startDate.getTime())) {
      errors.push('Invalid start date format');
    }
    
    if (isNaN(nextResetDate.getTime())) {
      errors.push('Invalid next reset date format');
    }
    
    if (startDate >= nextResetDate) {
      errors.push('Start date must be before next reset date');
    }
    
    // Check if next reset date is reasonable (not too far in future)
    const maxFutureDate = new Date();
    maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 1);
    
    if (nextResetDate > maxFutureDate) {
      errors.push('Next reset date is too far in the future');
    }
    
  } catch (error) {
    errors.push('Error validating dates: ' + error.message);
  }
  
  return errors;
};

/**
 * Validate topup purchase
 * @param {Object} topup - Topup package data
 * @param {Object} company - Company data
 * @returns {Object} - Validation result
 */
export const validateTopupPurchase = (topup, company) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!topup) {
      errors.push('Topup package is required');
      return { isValid: false, errors, warnings };
    }
    
    if (!company) {
      errors.push('Company data is required');
      return { isValid: false, errors, warnings };
    }
    
    // Validate topup package
    if (!topup.id && !topup.topupId) {
      errors.push('Topup ID is required');
    }
    
    if (!topup.isActive) {
      errors.push('Topup package is not active');
    }
    
    if (typeof topup.price !== 'number' || topup.price <= 0) {
      errors.push('Topup price must be a positive number');
    }
    
    if (!topup.limits) {
      errors.push('Topup limits are required');
    }
    
    // Check if company can purchase this topup
    if (company.subscription?.basePlan?.planId === 'trial') {
      warnings.push('Company is on trial plan - consider upgrading to a paid plan first');
    }
    
    // Check for duplicate active topups
    const activeTopups = company.subscription?.topups?.filter(t => t.status === 'active') || [];
    const duplicateTopup = activeTopups.find(t => t.topupId === topup.topupId);
    if (duplicateTopup) {
      warnings.push('Company already has an active topup of this type');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating topup purchase:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};

/**
 * Validate plan upgrade
 * @param {Object} currentSubscription - Current subscription data
 * @param {Object} newPlan - New plan data
 * @returns {Object} - Validation result
 */
export const validateUpgrade = (currentSubscription, newPlan) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!currentSubscription) {
      errors.push('Current subscription is required');
      return { isValid: false, errors, warnings };
    }
    
    if (!newPlan) {
      errors.push('New plan is required');
      return { isValid: false, errors, warnings };
    }
    
    const currentPlan = currentSubscription.basePlan;
    
    // Check if upgrade is valid
    if (currentPlan.planId === newPlan.planId) {
      errors.push('Cannot upgrade to the same plan');
    }
    
    // Check plan hierarchy (basic -> pro -> enterprise)
    const planHierarchy = ['starter', 'basic', 'pro', 'enterprise'];
    const currentIndex = planHierarchy.indexOf(currentPlan.planId);
    const newIndex = planHierarchy.indexOf(newPlan.planId);
    
    if (currentIndex === -1) {
      errors.push('Current plan ID is not recognized');
    }
    
    if (newIndex === -1) {
      errors.push('New plan ID is not recognized');
    }
    
    if (currentIndex >= 0 && newIndex >= 0 && newIndex <= currentIndex) {
      warnings.push('This is a downgrade, not an upgrade');
    }
    
    // Check if current subscription is active
    const now = new Date();
    const nextResetDate = new Date(currentPlan.nextResetDate);
    
    if (now > nextResetDate) {
      errors.push('Current subscription has expired');
    }
    
    // Validate new plan data
    if (typeof newPlan.price !== 'number' || newPlan.price <= 0) {
      errors.push('New plan price must be a positive number');
    }
    
    if (!newPlan.limits) {
      errors.push('New plan limits are required');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating upgrade:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};

/**
 * Validate payment data
 * @param {Object} paymentData - Payment data
 * @returns {Object} - Validation result
 */
export const validatePayment = (paymentData) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!paymentData) {
      errors.push('Payment data is required');
      return { isValid: false, errors, warnings };
    }
    
    // Required fields
    if (!paymentData.companyId) {
      errors.push('Company ID is required');
    }
    
    if (!paymentData.amount) {
      errors.push('Payment amount is required');
    } else if (typeof paymentData.amount !== 'number' || paymentData.amount <= 0) {
      errors.push('Payment amount must be a positive number');
    }
    
    if (!paymentData.type) {
      errors.push('Payment type is required');
    } else if (!['plan_upgrade', 'topup_purchase', 'subscription_renewal'].includes(paymentData.type)) {
      errors.push('Payment type must be plan_upgrade, topup_purchase, or subscription_renewal');
    }
    
    if (!paymentData.method) {
      errors.push('Payment method is required');
    } else if (!['stripe', 'razorpay', 'manual', 'bank_transfer'].includes(paymentData.method)) {
      errors.push('Payment method must be stripe, razorpay, manual, or bank_transfer');
    }
    
    if (!paymentData.status) {
      errors.push('Payment status is required');
    } else if (!['pending', 'completed', 'failed', 'refunded'].includes(paymentData.status)) {
      errors.push('Payment status must be pending, completed, failed, or refunded');
    }
    
    // Optional fields validation
    if (paymentData.reference && typeof paymentData.reference !== 'string') {
      errors.push('Payment reference must be a string');
    }
    
    if (paymentData.currency && typeof paymentData.currency !== 'string') {
      errors.push('Payment currency must be a string');
    }
    
    if (paymentData.metadata && typeof paymentData.metadata !== 'object') {
      errors.push('Payment metadata must be an object');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating payment:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};

/**
 * Validate company data for subscription operations
 * @param {Object} company - Company data
 * @returns {Object} - Validation result
 */
export const validateCompany = (company) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!company) {
      errors.push('Company data is required');
      return { isValid: false, errors, warnings };
    }
    
    if (!company.id) {
      errors.push('Company ID is required');
    }
    
    if (!company.name) {
      errors.push('Company name is required');
    }
    
    if (!company.owner) {
      errors.push('Company owner is required');
    } else {
      if (!company.owner.userId) {
        errors.push('Company owner user ID is required');
      }
      
      if (!company.owner.email) {
        errors.push('Company owner email is required');
      }
    }
    
    if (company.status && !['active', 'inactive', 'suspended'].includes(company.status)) {
      errors.push('Company status must be active, inactive, or suspended');
    }
    
    if (company.status === 'suspended') {
      warnings.push('Company is suspended - subscription operations may be restricted');
    }
    
    if (company.status === 'inactive') {
      warnings.push('Company is inactive - subscription operations may be restricted');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating company:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};

/**
 * Validate migration data
 * @param {Object} migrationData - Migration data
 * @returns {Object} - Validation result
 */
export const validateMigration = (migrationData) => {
  try {
    const errors = [];
    const warnings = [];
    
    if (!migrationData) {
      errors.push('Migration data is required');
      return { isValid: false, errors, warnings };
    }
    
    if (!migrationData.companyId) {
      errors.push('Company ID is required for migration');
    }
    
    if (!migrationData.fromStructure) {
      errors.push('Source structure is required');
    }
    
    if (!migrationData.toStructure) {
      errors.push('Target structure is required');
    }
    
    if (migrationData.fromStructure === migrationData.toStructure) {
      warnings.push('Source and target structures are the same');
    }
    
    if (!migrationData.migratedBy) {
      errors.push('Migration performed by is required');
    }
    
    if (!migrationData.migratedAt) {
      errors.push('Migration timestamp is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    console.error('Error validating migration:', error);
    return {
      isValid: false,
      errors: ['Validation error: ' + error.message],
      warnings: []
    };
  }
};